import React from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const colors = [
  '#4facfe', '#43e97b', '#f441a5', '#ffce00',
  '#ff5f6d', '#845ec2', '#ffc75f', '#f9f871',
  '#00c9a7', '#f67280', '#4bc0c0', '#36a2eb',
  '#9c88ff', '#ffa726', '#66bb6a', '#ef5350'
];

export const SchemePieChart = ({ datasets, selectedScheme, optionsScheme, selectedState }) => {
  // Count surveys by state with proper handling for different data structures
  const stateCounts = {};

  datasets.forEach((dataset) => {
    if (selectedScheme === "15") {
      // RKVY scheme - group by state and sum up the total values
      dataset.responseArray?.forEach(item => {
        const stateName = item.stateName || item.state || item.gisStateName || 'Unknown State';
        // When state filter is applied, the data is already filtered, so we don't need to filter again
        const count = parseFloat(item.total) || 0;
        if (count > 0) {
          stateCounts[stateName] = (stateCounts[stateName] || 0) + count;
        }
      });
    } else if (selectedScheme === "28") {
      // Patanjali scheme - group features by state
      dataset.responseArray?.features?.forEach(feature => {
        const stateName = feature.properties?.stateName || 
                         feature.properties?.state || 
                         feature.properties?.gisStateName || 
                         'Unknown State';
        // When state filter is applied, the data is already filtered, so we don't need to filter again
        stateCounts[stateName] = (stateCounts[stateName] || 0) + 1;
      });
    } else {
      // Regular schemes - group by state
      dataset.responseArray?.forEach(item => {
        const stateName = item.stateName || 
                         item.state || 
                         item.gisStateName || 
                         item.statename ||
                         'Unknown State';
        // When state filter is applied, the data is already filtered, so we don't need to filter again
        stateCounts[stateName] = (stateCounts[stateName] || 0) + 1;
      });
    }
  });

  // If no data found, show a message
  if (Object.keys(stateCounts).length === 0) {
    return (
      <div style={{ 
        padding: '40px 20px', 
        maxWidth: '500px', 
        margin: '0 auto', 
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '2px dashed #dee2e6'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
        <h3 style={{ color: '#6c757d', marginBottom: '8px' }}>No Data Available</h3>
        <p style={{ color: '#adb5bd', margin: 0 }}>
          {selectedState ? 
            `No surveys found for ${selectedState} with the selected filters.` :
            'Please select a scheme and apply filters to view the pie chart.'}
        </p>
      </div>
    );
  }

  const stateNames = Object.keys(stateCounts);
  const surveyCounts = stateNames.map(name => stateCounts[name]);
  const totalSurveys = surveyCounts.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels: stateNames,
    datasets: [
      {
        data: surveyCounts,
        backgroundColor: stateNames.map((_, i) => colors[i % colors.length]),
        borderColor: '#fff',
        borderWidth: 3,
        hoverBorderWidth: 5,
        hoverBorderColor: '#fff',
        hoverBackgroundColor: stateNames.map((_, i) => {
          const color = colors[i % colors.length];
          return color + 'dd';
        }),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { 
            size: 10,
            weight: '600',
            family: 'system-ui, -apple-system, sans-serif'
          },
          color: '#495057',
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const percentage = ((value / totalSurveys) * 100).toFixed(1);
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  strokeStyle: data.datasets[0].borderColor,
                  lineWidth: data.datasets[0].borderWidth,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        },
      },
      title: {
        display: true,
        text: selectedState ? 
          `Survey Distribution for ${selectedState}` :
          'Survey Distribution by State',
        font: {
          size: 20,
          weight: 'bold',
          family: 'system-ui, -apple-system, sans-serif'
        },
        color: '#212529',
        padding: { top: 10, bottom: 30 },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const value = context.parsed;
            const percentage = ((value / totalSurveys) * 100).toFixed(1);
            return [
              `Count: ${value.toLocaleString()} surveys`,
              `Percentage: ${percentage}%`
            ];
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
    },
  };

  return (
    <div className='container'>
      <div className='row'>
        <div className='col-md-12'>
          <div className='row mt-12'>
            <div className='col-md-12 leftbar'>
              <div style={{ 
                padding: '20px', 
                margin: '20px auto',
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e9ecef'
              }}>
                {/* Header with total count */}
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    color: '#495057',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    🗺️ Total Surveys: {totalSurveys.toLocaleString()}
                  </h4>
                  {stateNames.length > 1 && (
                    <p style={{ 
                      margin: '5px 0 0 0', 
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      Distributed across {stateNames.length} state{stateNames.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                {/* Chart container */}
                <div style={{ 
                  height: '400px',
                  position: 'relative'
                }}>
                  <Pie data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
